import React, { useMemo } from 'react';

import { useProductionForecastStore } from '../../hooks/useProductionForecastStore';

import type {
  DeliverabilityTestPoint,
  DeliverabilityTestType,
} from './types';

import { ETH } from '../economics/ecoShared';

import {
  calcBinomialAof,
  calcExponentialAof,
  fitBinomial,
  fitExponential,
  pressureSquaredDrop,
  solveBinomialRate,
} from './calculations';

export default function DeliverabilityPanel({
  projectId,
}: {
  projectId: number;
}) {
  const project = useProductionForecastStore(
    (state) => state.projects[String(projectId)],
  );

  const setTestTypeInStore =
    useProductionForecastStore(
      (state) =>
        state.setDeliverabilityTestType,
    );

  const addPointInStore =
    useProductionForecastStore(
      (state) =>
        state.addDeliverabilityPoint,
    );

  const updatePointInStore =
    useProductionForecastStore(
      (state) =>
        state.updateDeliverabilityPoint,
    );

  const deletePointInStore =
    useProductionForecastStore(
      (state) =>
        state.deleteDeliverabilityPoint,
    );

  const resetInStore =
    useProductionForecastStore(
      (state) =>
        state.resetDeliverability,
    );

  const deliverability =
    project?.deliverability;

  const baseReservoirPressure =
    project?.params.reservoirPressure ?? '';

  const testType:
    DeliverabilityTestType =
    deliverability?.testType ??
    '稳定试井';

  const points:
    DeliverabilityTestPoint[] =
    deliverability?.points ?? [];

  const validPoints = useMemo(() => {
    return points
      .filter(
        (point) =>
          point.reservoirPressure !== '' &&
          point.bottomHolePressure !== '' &&
          point.gasRate !== '',
      )
      .map((point) => ({
        reservoirPressure:
          Number(
            point.reservoirPressure,
          ),

        bottomHolePressure:
          Number(
            point.bottomHolePressure,
          ),

        gasRate:
          Number(point.gasRate),
      }))
      .filter(
        (point) =>
          Number.isFinite(
            point.reservoirPressure,
          ) &&
          Number.isFinite(
            point.bottomHolePressure,
          ) &&
          Number.isFinite(
            point.gasRate,
          ) &&
          point.gasRate > 0 &&
          point.reservoirPressure >
            point.bottomHolePressure,
      );
  }, [points]);

  const averageReservoirPressure =
    useMemo(() => {
      if (
        validPoints.length === 0
      ) {
        return null;
      }

      return (
        validPoints.reduce(
          (sum, point) =>
            sum +
            point.reservoirPressure,
          0,
        ) / validPoints.length
      );
    }, [validPoints]);

  const pressureConsistency =
    useMemo(() => {
      if (
        validPoints.length < 2
      ) {
        return null;
      }

      const values =
        validPoints.map(
          (point) =>
            point.reservoirPressure,
        );

      const min =
        Math.min(...values);

      const max =
        Math.max(...values);

      const avg =
        values.reduce(
          (sum, value) =>
            sum + value,
          0,
        ) / values.length;

      const spread =
        avg > 0
          ? ((max - min) / avg) *
            100
          : 0;

      return {
        min,
        max,
        spread,
      };
    }, [validPoints]);

  const binomial = useMemo(() => {
    return fitBinomial(
      validPoints,
    );
  }, [validPoints]);

  const exponential =
    useMemo(() => {
      return fitExponential(
        validPoints,
      );
    }, [validPoints]);

  const binomialAof =
    useMemo(() => {
      if (
        !binomial ||
        averageReservoirPressure ===
          null
      ) {
        return null;
      }

      return calcBinomialAof(
        averageReservoirPressure,
        binomial.A,
        binomial.B,
      );
    }, [
      binomial,
      averageReservoirPressure,
    ]);

  const exponentialAof =
    useMemo(() => {
      if (
        !exponential ||
        averageReservoirPressure ===
          null
      ) {
        return null;
      }

      return calcExponentialAof(
        averageReservoirPressure,
        exponential.C,
        exponential.n,
      );
    }, [
      exponential,
      averageReservoirPressure,
    ]);

  if (!deliverability) {
    return (
      <div
        style={{
          padding: 20,
          color: ETH.textMuted,
        }}
      >
        正在初始化产能试井数据...
      </div>
    );
  }

  const setTestType = (
    value: DeliverabilityTestType,
  ) => {
    setTestTypeInStore(
      projectId,
      value,
    );
  };

  const updatePoint = (
    id: number,
    key: Exclude<
      keyof DeliverabilityTestPoint,
      'id'
    >,
    value: string,
  ) => {
    updatePointInStore(
      projectId,
      id,
      key,
      value,
    );
  };

  const addPoint = () => {
    addPointInStore(projectId);
  };

  const deletePoint = (
    id: number,
  ) => {
    deletePointInStore(
      projectId,
      id,
    );
  };

  const reset = () => {
    resetInStore(projectId);
  };

  const fillBaseReservoirPressure =
    () => {
      if (
        baseReservoirPressure.trim() ===
        ''
      ) {
        return;
      }

      points.forEach((point) => {
        if (
          point.reservoirPressure.trim() ===
          ''
        ) {
          updatePointInStore(
            projectId,
            point.id,
            'reservoirPressure',
            baseReservoirPressure,
          );
        }
      });
    };

  return (
    <div>
      {/* 标题 */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>
            产能试井
          </h3>

          <div
            style={{
              marginTop: 5,
              fontSize: 11,
              color: ETH.textMuted,
            }}
          >
            基于多工作制度测试点开展气井产能方程拟合、
            AOF评价及采气指示曲线分析
          </div>
        </div>

        <div
          style={{
            textAlign: 'right',
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: ETH.textMuted,
            }}
          >
            有效测试点
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 20,
              fontWeight: 800,
              fontFamily:
                ETH.fontMono,
            }}
          >
            {validPoints.length}
          </div>
        </div>
      </div>

      {/* 数据联动 */}
      <div
        style={{
          padding: 14,
          marginBottom: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 20,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color:
                  ETH.textMuted,
              }}
            >
              数据准备中的原始地层压力
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 16,
                fontWeight: 700,
                fontFamily:
                  ETH.fontMono,
              }}
            >
              {baseReservoirPressure
                ? `${baseReservoirPressure} MPa`
                : '未填写'}
            </div>
          </div>

          <button
            type="button"
            disabled={
              !baseReservoirPressure
            }
            onClick={
              fillBaseReservoirPressure
            }
            style={{
              ...buttonStyle,
              opacity:
                baseReservoirPressure
                  ? 1
                  : 0.5,
              cursor:
                baseReservoirPressure
                  ? 'pointer'
                  : 'not-allowed',
            }}
          >
            带入空白测试点
          </button>

          <div
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              color:
                ETH.textMuted,
            }}
          >
            已有测试点压力不会被覆盖
          </div>
        </div>
      </div>

      {/* 试井设置 */}
      <div
        style={{
          padding: 14,
          marginBottom: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            试井制度
          </span>

          <select
            value={testType}
            onChange={(event) =>
              setTestType(
                event.target
                  .value as DeliverabilityTestType,
              )
            }
            style={inputStyle}
          >
            <option value="稳定试井">
              稳定试井
            </option>

            <option
              value="等时试井"
              disabled
            >
              等时试井（待接入时段修正）
            </option>

            <option
              value="修正等时试井"
              disabled
            >
              修正等时试井（待接入时段修正）
            </option>
          </select>

          <div
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              color: ETH.textMuted,
            }}
          >
            当前采用压力平方形式进行产能拟合
          </div>
        </div>
      </div>

      {/* 测试数据 */}
      <div
        style={{
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent:
              'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              产能测试数据
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color:
                  ETH.textMuted,
              }}
            >
              至少录入3个有效测试点；建议采用多个工作制度进行拟合
            </div>
          </div>

          <button
            type="button"
            onClick={addPoint}
            style={buttonStyle}
          >
            + 新增测试点
          </button>
        </div>

        <div
          style={{
            overflowX: 'auto',
          }}
        >
          <table
            style={{
              width: '100%',
              minWidth: 900,
              borderCollapse:
                'collapse',
              fontSize: 11,
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>
                  序号
                </th>

                <th style={thStyle}>
                  地层压力 Pr / MPa
                </th>

                <th style={thStyle}>
                  井底流压 Pwf / MPa
                </th>

                <th style={thStyle}>
                  产气量 q / 10⁴m³/d
                </th>

                <th style={thStyle}>
                  Pr²-Pwf² / MPa²
                </th>

                <th style={thStyle}>
                  操作
                </th>
              </tr>
            </thead>

            <tbody>
              {points.map(
                (
                  point,
                  index,
                ) => {
                  const pr =
                    Number(
                      point.reservoirPressure,
                    );

                  const pwf =
                    Number(
                      point.bottomHolePressure,
                    );

                  const delta =
                    point.reservoirPressure !==
                      '' &&
                    point.bottomHolePressure !==
                      '' &&
                    Number.isFinite(
                      pr,
                    ) &&
                    Number.isFinite(
                      pwf,
                    ) &&
                    pr > pwf
                      ? pressureSquaredDrop(
                          pr,
                          pwf,
                        )
                      : null;

                  return (
                    <tr
                      key={point.id}
                    >
                      <td
                        style={{
                          ...tdStyle,
                          textAlign:
                            'center',
                        }}
                      >
                        {index + 1}
                      </td>

                      <InputCell
                        value={
                          point.reservoirPressure
                        }
                        onChange={(
                          value,
                        ) =>
                          updatePoint(
                            point.id,
                            'reservoirPressure',
                            value,
                          )
                        }
                      />

                      <InputCell
                        value={
                          point.bottomHolePressure
                        }
                        onChange={(
                          value,
                        ) =>
                          updatePoint(
                            point.id,
                            'bottomHolePressure',
                            value,
                          )
                        }
                      />

                      <InputCell
                        value={
                          point.gasRate
                        }
                        onChange={(
                          value,
                        ) =>
                          updatePoint(
                            point.id,
                            'gasRate',
                            value,
                          )
                        }
                      />

                      <td
                        style={{
                          ...tdStyle,
                          textAlign:
                            'right',
                          fontFamily:
                            ETH.fontMono,
                        }}
                      >
                        {delta ===
                        null
                          ? '--'
                          : delta.toFixed(
                              3,
                            )}
                      </td>

                      <td
                        style={{
                          ...tdStyle,
                          textAlign:
                            'center',
                        }}
                      >
                        <button
                          type="button"
                          disabled={
                            points.length <=
                            3
                          }
                          onClick={() =>
                            deletePoint(
                              point.id,
                            )
                          }
                          style={{
                            border:
                              'none',
                            background:
                              'transparent',
                            color:
                              ETH.textMuted,
                            cursor:
                              points.length <=
                              3
                                ? 'not-allowed'
                                : 'pointer',
                          }}
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  );
                },
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 数据质量检查 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(3, minmax(0, 1fr))',
          gap: 12,
          marginTop: 14,
        }}
      >
        <QualityCard
          title="有效测试点"
          value={`${validPoints.length} 个`}
          description={
            validPoints.length >= 3
              ? '已满足拟合最低数据要求'
              : '至少需要3个有效测试点'
          }
        />

        <QualityCard
          title="平均地层压力"
          value={
            averageReservoirPressure ===
            null
              ? '--'
              : `${averageReservoirPressure.toFixed(
                  3,
                )} MPa`
          }
          description="用于当前AOF计算"
        />

        <QualityCard
          title="地层压力离散度"
          value={
            pressureConsistency ===
            null
              ? '--'
              : `${pressureConsistency.spread.toFixed(
                  2,
                )}%`
          }
          description={
            pressureConsistency ===
            null
              ? '等待多个测试点'
              : `范围 ${pressureConsistency.min.toFixed(
                  2,
                )}–${pressureConsistency.max.toFixed(
                  2,
                )} MPa`
          }
        />
      </div>

      {/* 模型参数 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            '1fr 1fr',
          gap: 14,
          marginTop: 14,
        }}
      >
        <ModelCard
          title="二项式产能方程"
          equation="Pr² - Pwf² = Aq + Bq²"
          ready={Boolean(
            binomial,
          )}
        >
          <ResultLine
            label="A"
            value={
              binomial
                ? formatNumber(
                    binomial.A,
                  )
                : '--'
            }
          />

          <ResultLine
            label="B"
            value={
              binomial
                ? formatNumber(
                    binomial.B,
                  )
                : '--'
            }
          />

          <ResultLine
            label="R²"
            value={
              binomial
                ? formatNumber(
                    binomial.r2,
                    4,
                  )
                : '--'
            }
          />

          <ResultLine
            label="RMSE"
            value={
              binomial
                ? formatNumber(
                    binomial.rmse,
                    4,
                  )
                : '--'
            }
          />

          <ResultLine
            label="AOF"
            value={
              binomialAof ===
              null
                ? '--'
                : `${formatNumber(
                    binomialAof,
                    3,
                  )} ×10⁴m³/d`
            }
            strong
          />
        </ModelCard>

        <ModelCard
          title="指数式产能方程"
          equation="q = C(Pr² - Pwf²)ⁿ"
          ready={Boolean(
            exponential,
          )}
        >
          <ResultLine
            label="C"
            value={
              exponential
                ? formatNumber(
                    exponential.C,
                  )
                : '--'
            }
          />

          <ResultLine
            label="n"
            value={
              exponential
                ? formatNumber(
                    exponential.n,
                    4,
                  )
                : '--'
            }
          />

          <ResultLine
            label="R²"
            value={
              exponential
                ? formatNumber(
                    exponential.r2,
                    4,
                  )
                : '--'
            }
          />

          <ResultLine
            label="RMSE"
            value={
              exponential
                ? formatNumber(
                    exponential.rmse,
                    4,
                  )
                : '--'
            }
          />

          <ResultLine
            label="AOF"
            value={
              exponentialAof ===
              null
                ? '--'
                : `${formatNumber(
                    exponentialAof,
                    3,
                  )} ×10⁴m³/d`
            }
            strong
          />
        </ModelCard>
      </div>

      {/* 采气指示曲线 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            采气指示曲线
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              color:
                ETH.textMuted,
            }}
          >
            横轴为产气量，
            纵轴为井底流压；
            同时显示实测点、
            二项式拟合与指数式拟合
          </div>
        </div>

        <div
          style={{
            marginTop: 12,
          }}
        >
          <DeliverabilityChart
            reservoirPressure={
              averageReservoirPressure
            }
            points={
              validPoints
            }
            binomial={
              binomial
            }
            exponential={
              exponential
            }
            binomialAof={
              binomialAof
            }
            exponentialAof={
              exponentialAof
            }
          />
        </div>
      </div>

      {/* 结果说明 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          结果说明
        </div>

        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            lineHeight: 1.8,
            color: ETH.textMuted,
          }}
        >
          <div>
            · 二项式模型：
            Pr²-Pwf²=Aq+Bq²。
          </div>

          <div>
            · 指数式模型：
            q=C(Pr²-Pwf²)ⁿ。
          </div>

          <div>
            ·
            R²和RMSE用于评价数值拟合程度，但模型选择还应结合试井制度及物理合理性。
          </div>

          <div>
            · AOF
            当前按井底流压约
            0.101 MPa计算。
          </div>

          <div>
            ·
            当前实现的是压力平方法；高压气井后续可继续扩展气体拟压力方法。
          </div>

          <div>
            ·
            等时试井及修正等时试井尚未接入时段修正，因此当前保持禁用。
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent:
            'flex-end',
          marginTop: 14,
        }}
      >
        <button
          type="button"
          onClick={reset}
          style={buttonStyle}
        >
          重置测试数据
        </button>
      </div>
    </div>
  );
}

function DeliverabilityChart({
  reservoirPressure,
  points,
  binomial,
  exponential,
  binomialAof,
  exponentialAof,
}: {
  reservoirPressure:
    number | null;

  points: {
    reservoirPressure: number;
    bottomHolePressure: number;
    gasRate: number;
  }[];

  binomial:
    ReturnType<
      typeof fitBinomial
    >;

  exponential:
    ReturnType<
      typeof fitExponential
    >;

  binomialAof:
    number | null;

  exponentialAof:
    number | null;
}) {
  if (
    reservoirPressure === null ||
    points.length < 3 ||
    (!binomial &&
      !exponential)
  ) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            'center',
          border: `1px dashed ${ETH.headerBorder}`,
          color: ETH.textMuted,
          fontSize: 12,
        }}
      >
        至少录入3个有效测试点后显示采气指示曲线
      </div>
    );
  }

  const width = 900;
  const height = 340;

  const left = 62;
  const right = 28;
  const top = 24;
  const bottom = 48;

  const plotWidth =
    width - left - right;

  const plotHeight =
    height - top - bottom;

  const measuredMax =
    Math.max(
      ...points.map(
        (point) =>
          point.gasRate,
      ),
    );

  const modelMax =
    Math.max(
      binomialAof &&
        Number.isFinite(
          binomialAof,
        )
        ? binomialAof
        : 0,

      exponentialAof &&
        Number.isFinite(
          exponentialAof,
        )
        ? exponentialAof
        : 0,

      measuredMax,
    );

  const maxRate =
    Math.max(
      1,
      modelMax * 1.08,
    );

  const maxPressure =
    reservoirPressure *
    1.02;

  const x = (
    rate: number,
  ) =>
    left +
    (rate / maxRate) *
      plotWidth;

  const y = (
    pressure: number,
  ) =>
    top +
    (1 -
      pressure /
        maxPressure) *
      plotHeight;

  const samples =
    Array.from(
      {
        length: 61,
      },
      (_, index) => {
        const fraction =
          index / 60;

        return (
          reservoirPressure *
            (1 - fraction) +
          0.101 * fraction
        );
      },
    );

  const binomialCurve =
    binomial
      ? samples
          .map(
            (
              pressure,
            ) => {
              const delta =
                pressureSquaredDrop(
                  reservoirPressure,
                  pressure,
                );

              const rate =
                solveBinomialRate(
                  delta,
                  binomial.A,
                  binomial.B,
                );

              return {
                pressure,
                rate,
              };
            },
          )
          .filter(
            (point) =>
              Number.isFinite(
                point.rate,
              ) &&
              point.rate >= 0,
          )
      : [];

  const exponentialCurve =
    exponential
      ? samples
          .map(
            (
              pressure,
            ) => {
              const delta =
                pressureSquaredDrop(
                  reservoirPressure,
                  pressure,
                );

              return {
                pressure,
                rate:
                  exponential.C *
                  delta **
                    exponential.n,
              };
            },
          )
          .filter(
            (point) =>
              Number.isFinite(
                point.rate,
              ) &&
              point.rate >= 0,
          )
      : [];

  const makePath = (
    curve: {
      pressure: number;
      rate: number;
    }[],
  ) => {
    return curve
      .map(
        (
          point,
          index,
        ) =>
          `${
            index === 0
              ? 'M'
              : 'L'
          } ${x(
            point.rate,
          ).toFixed(
            2,
          )} ${y(
            point.pressure,
          ).toFixed(2)}`,
      )
      .join(' ');
  };

  const xTicks =
    Array.from(
      {
        length: 6,
      },
      (_, index) =>
        (maxRate / 5) *
        index,
    );

  const yTicks =
    Array.from(
      {
        length: 6,
      },
      (_, index) =>
        (maxPressure / 5) *
        index,
    );

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: '100%',
          minWidth: 720,
          height: 340,
          display: 'block',
        }}
      >
        {/* 背景 */}
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={ETH.bg}
        />

        {/* 横向网格 */}
        {yTicks.map(
          (value) => (
            <g key={value}>
              <line
                x1={left}
                x2={
                  width - right
                }
                y1={y(value)}
                y2={y(value)}
                stroke={
                  ETH.headerBorder
                }
                strokeWidth={1}
              />

              <text
                x={left - 10}
                y={
                  y(value) + 4
                }
                textAnchor="end"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {value.toFixed(
                  1,
                )}
              </text>
            </g>
          ),
        )}

        {/* 纵向网格 */}
        {xTicks.map(
          (value) => (
            <g key={value}>
              <line
                x1={x(value)}
                x2={x(value)}
                y1={top}
                y2={
                  height -
                  bottom
                }
                stroke={
                  ETH.headerBorder
                }
                strokeWidth={1}
              />

              <text
                x={x(value)}
                y={
                  height -
                  bottom +
                  18
                }
                textAnchor="middle"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {value.toFixed(
                  1,
                )}
              </text>
            </g>
          ),
        )}

        {/* 坐标轴 */}
        <line
          x1={left}
          x2={left}
          y1={top}
          y2={
            height - bottom
          }
          stroke={ETH.textSec}
          strokeWidth={1.2}
        />

        <line
          x1={left}
          x2={
            width - right
          }
          y1={
            height - bottom
          }
          y2={
            height - bottom
          }
          stroke={ETH.textSec}
          strokeWidth={1.2}
        />

        {/* 二项式拟合 */}
        {binomial &&
          binomialCurve.length >
            1 && (
            <path
              d={makePath(
                binomialCurve,
              )}
              fill="none"
              stroke="#b64f35"
              strokeWidth={2.2}
            />
          )}

        {/* 指数式拟合 */}
        {exponential &&
          exponentialCurve.length >
            1 && (
            <path
              d={makePath(
                exponentialCurve,
              )}
              fill="none"
              stroke="#3d6f91"
              strokeWidth={2.2}
              strokeDasharray="7 5"
            />
          )}

        {/* 实测点 */}
        {points.map(
          (
            point,
            index,
          ) => (
            <g
              key={`${point.gasRate}-${point.bottomHolePressure}-${index}`}
            >
              <circle
                cx={x(
                  point.gasRate,
                )}
                cy={y(
                  point.bottomHolePressure,
                )}
                r={4.5}
                fill={ETH.text}
              />

              <text
                x={
                  x(
                    point.gasRate,
                  ) + 7
                }
                y={
                  y(
                    point.bottomHolePressure,
                  ) - 7
                }
                fontSize={9}
                fill={
                  ETH.textMuted
                }
              >
                {index + 1}
              </text>
            </g>
          ),
        )}

        {/* 图例 */}
        <line
          x1={left + 20}
          x2={left + 50}
          y1={top + 12}
          y2={top + 12}
          stroke="#b64f35"
          strokeWidth={2.2}
        />

        <text
          x={left + 56}
          y={top + 16}
          fontSize={10}
          fill={ETH.textSec}
        >
          二项式
        </text>

        <line
          x1={left + 130}
          x2={left + 160}
          y1={top + 12}
          y2={top + 12}
          stroke="#3d6f91"
          strokeWidth={2.2}
          strokeDasharray="7 5"
        />

        <text
          x={left + 166}
          y={top + 16}
          fontSize={10}
          fill={ETH.textSec}
        >
          指数式
        </text>

        <circle
          cx={left + 250}
          cy={top + 12}
          r={4}
          fill={ETH.text}
        />

        <text
          x={left + 260}
          y={top + 16}
          fontSize={10}
          fill={ETH.textSec}
        >
          实测点
        </text>

        {/* 坐标轴名称 */}
        <text
          x={
            left +
            plotWidth / 2
          }
          y={height - 8}
          textAnchor="middle"
          fontSize={11}
          fill={ETH.textSec}
        >
          产气量 q / 10⁴m³/d
        </text>

        <text
          x={16}
          y={
            top +
            plotHeight / 2
          }
          textAnchor="middle"
          fontSize={11}
          fill={ETH.textSec}
          transform={`rotate(-90 16 ${
            top +
            plotHeight / 2
          })`}
        >
          井底流压 Pwf / MPa
        </text>
      </svg>
    </div>
  );
}

function InputCell({
  value,
  onChange,
}: {
  value: string;
  onChange: (
    value: string,
  ) => void;
}) {
  return (
    <td style={tdStyle}>
      <input
        type="number"
        value={value}
        onChange={(event) =>
          onChange(
            event.target.value,
          )
        }
        style={tableInputStyle}
      />
    </td>
  );
}

function ModelCard({
  title,
  equation,
  ready,
  children,
}: {
  title: string;
  equation: string;
  ready: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {title}
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              color:
                ETH.textMuted,
              fontFamily:
                ETH.fontMono,
            }}
          >
            {equation}
          </div>
        </div>

        <span
          style={{
            fontSize: 10,
            color: ready
              ? ETH.ok
              : ETH.textMuted,
          }}
        >
          {ready
            ? '已完成拟合'
            : '等待数据'}
        </span>
      </div>

      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gap: 7,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ResultLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent:
          'space-between',
        alignItems: 'center',
        padding: strong
          ? '9px 0'
          : '5px 0',
        borderTop: strong
          ? `1px solid ${ETH.headerBorder}`
          : undefined,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {label}
      </span>

      <span
        style={{
          fontSize: strong
            ? 17
            : 12,
          fontWeight: strong
            ? 800
            : 600,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function QualityCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string;
  description: string;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 20,
          fontWeight: 800,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
}

function formatNumber(
  value: number,
  digits = 6,
) {
  if (
    !Number.isFinite(value)
  ) {
    return '无效';
  }

  if (
    Math.abs(value) >
      1e5 ||
    (Math.abs(value) >
      0 &&
      Math.abs(value) <
        1e-4)
  ) {
    return value.toExponential(
      4,
    );
  }

  return value.toFixed(
    digits,
  );
}

const inputStyle: React.CSSProperties = {
  minWidth: 210,
  padding: '6px 8px',
  border: `1px solid ${ETH.headerBorder}`,
  background: ETH.bg,
  color: ETH.text,
  outline: 'none',
};

const tableInputStyle: React.CSSProperties = {
  width: '100%',
  minWidth: 120,
  boxSizing: 'border-box',
  padding: '6px 8px',
  border: `1px solid ${ETH.headerBorder}`,
  background: ETH.bg,
  color: ETH.text,
  outline: 'none',
};

const thStyle: React.CSSProperties = {
  padding: '8px',
  borderBottom: `1px solid ${ETH.headerBorder}`,
  textAlign: 'center',
  color: ETH.textSec,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '6px',
  borderBottom: `1px solid ${ETH.headerBorder}`,
};

const buttonStyle: React.CSSProperties = {
  padding: '7px 12px',
  border: `1px solid ${ETH.headerBorder}`,
  background: ETH.canvasBg,
  color: ETH.text,
  cursor: 'pointer',
};